import { InfoOutlined } from '@mui/icons-material';
import { Alert, Avatar, Box, Card, CardActions, CardContent, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { ChangeEvent, FormEventHandler } from 'react';
import { Interpolation, InterpolationItem } from '../../../config/i18n/components/Interpolation';
import { JiraUtils } from '../../../integrations/jira/utils/JiraUtils';
import { DangerButton, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { Translation, Resources } from '../../../@types/i18next';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { EXTERNAL_URLS } from '../../../routing/routes';

type Props = {
  initialValue: string;
  value: string;
  onValueChange: (value: string) => void;
  onSaveClick: () => void;
  isSaveDisabled: boolean;
  onUnlinkClick: () => void;
};

type StepInstructionProps = {
  step: number;
  text: Translation<Resources>;
  items: InterpolationItem[] | undefined;
};

const StepInstruction = ({ step, text, items }: StepInstructionProps) => {
  return (
    <Box sx={{ display: 'flex' }}>
      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>{step}</Avatar>
      <Stack direction={'column'}>
        <Typography sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 500 }}>Step {step}</Typography>
        <Interpolation i18nKey={text} items={items} />
      </Stack>
    </Box>
  );
};

export const IssueCollectorCard = (props: Props) => {
  const { onValueChange, value, onSaveClick, isSaveDisabled, onUnlinkClick, initialValue } = props;
  const isChanged = value !== initialValue;
  const isErrorDisplayed = Boolean(isChanged && !JiraUtils.extractScriptUrlFromCode(value));
  const isJiraIssueCollectorConfigured = Boolean(initialValue);
  const steps = [
    {
      value: 1,
      text: 'integrationList.jiraIssueCollector.steps.step_1',
    },
    {
      value: 2,
      text: 'integrationList.jiraIssueCollector.steps.step_2',
    },
    {
      value: 3,
      text: 'integrationList.jiraIssueCollector.steps.step_3',
    },
    {
      value: 4,
      text: 'integrationList.jiraIssueCollector.steps.step_4',
      items: [{ name: 'docs', href: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_JIRA_ISSUE_COLLECTOR) }],
    },
    {
      value: 5,
      text: 'integrationList.jiraIssueCollector.steps.step_5',
    },
  ];

  const onInputValueChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onValueChange(event.target.value);
  };

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    onSaveClick();
  };

  return (
    <Card component="form" onSubmit={handleSubmit}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <Typography sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 500, mb: 1 }}>{t('jiraIssueCollector.inputHeader')}</Typography>
            <Interpolation i18nKey="integrationList.jiraIssueCollector.subtitle" />
            <TextField
              placeholder={t('jiraIssueCollector.defaultValue')}
              value={value}
              autoFocus={true}
              multiline={true}
              required={true}
              rows={10}
              onChange={onInputValueChange}
              error={isErrorDisplayed}
              label={t('jiraIssueCollector.inputLabel')}
              helperText={isErrorDisplayed ? t('jiraIssueCollector.inputError') : undefined}
              variant="filled"
              sx={{ width: 1, mt: 3 }}
            />
            <Box sx={{ color: 'text.secondary', display: 'flex', my: 2, gap: 1, alignItems: 'center' }}>
              <InfoOutlined fontSize="small" />
              <Interpolation i18nKey="jiraIssueCollector.description" items={[{ name: 'color_black', color: 'text.primary' }]} />
            </Box>
          </Grid>
          <Grid item xs={5}>
            <Stack direction="column" gap={2}>
              <Alert severity="info">
                <Interpolation i18nKey="integrationList.jiraIssueCollector.alert" />
              </Alert>
              {steps.map((step) => {
                return (
                  <StepInstruction key={step.value} step={step.value} text={step.text as Translation<Resources>} items={step.items as InterpolationItem[]} />
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: isJiraIssueCollectorConfigured ? 'space-between' : 'flex-end', p: 2 }}>
        <>
          {isJiraIssueCollectorConfigured && <DangerButton onClick={onUnlinkClick}>{t('jiraIssueCollector.unlink')}</DangerButton>}
          <PrimaryButton disabled={isErrorDisplayed || isSaveDisabled} type="submit">
            {t('jiraIssueCollector.save')}
          </PrimaryButton>
        </>
      </CardActions>
    </Card>
  );
};
